import { z } from 'zod';
import {
  CARGO_PROBLEM_DELETE_REASON_MAX,
  CARGO_PROBLEM_SEARCH_MIN,
  CargoProblemAttachmentContextSchema,
} from './vocab.js';
import { CargoProblemActorSchema } from './wire.js';

/**
 * @aiPurpose one attachment as the workspace material library and the chat
 *   bubble see it. Deliberately a NEW shape rather than a widening of
 *   `CargoProblemFileSchema`, which an old open tab still parses strictly.
 * @aiInvariant `context` and `messageId` are null on every row filed before the
 *   workspace shipped; nothing back-fills them, so legacy evidence stays
 *   readable exactly as it was written.
 * @aiInvariant deletion is soft and correlated in one direction only: no
 *   `deletedAt` means no actor and no reason. The reverse is NOT required — a
 *   draft author removing their own unpublished brief attachment owes no
 *   participant-visible reason (#2865 AC-10), and `deletedBy` goes null once a
 *   moderator is hard-deleted. The nonblank reason is required where it is
 *   owed, on the moderation request
 *   (`CargoProblemMaterialDeleteInputSchema`), so a legitimate row can never
 *   fail this projection.
 * @aiPii `fileName` is user-supplied display text. The storage `objectKey` is
 *   INTENTIONALLY absent: downloads are issued per file id, so the raw key
 *   never leaves the backend.
 */
export const CargoProblemMaterialSchema = z
  .object({
    id: z.string().uuid(),
    fileName: z.string().min(1),
    contentType: z.string().min(1),
    contentLength: z.number().int().positive(),
    context: CargoProblemAttachmentContextSchema.nullable(),
    messageId: z.string().uuid().nullable(),
    uploader: CargoProblemActorSchema,
    createdAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable(),
    deletedBy: CargoProblemActorSchema.nullable(),
    deletedReason: z.string().min(1).max(CARGO_PROBLEM_DELETE_REASON_MAX).nullable(),
  })
  .strict()
  .superRefine((material, ctx) => {
    if (material.deletedAt !== null) return;
    for (const path of ['deletedBy', 'deletedReason'] as const) {
      if (material[path] !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: 'deletion metadata requires deletedAt',
        });
      }
    }
  });
export type CargoProblemMaterial = z.infer<typeof CargoProblemMaterialSchema>;

/**
 * @aiPurpose the library's three tabs. `MEDIA` is the image and video MIME set and
 *   `DOCUMENTS` is `CARGO_PROBLEM_DOCUMENT_MIME`; together they partition the
 *   accepted allowlist, so `ALL` is their union and no file is unreachable.
 */
export const CargoProblemMaterialFilterSchema = z.enum(['ALL', 'MEDIA', 'DOCUMENTS']);
export type CargoProblemMaterialFilter = z.infer<typeof CargoProblemMaterialFilterSchema>;

/**
 * @aiPurpose query for the material library of one problem.
 * @aiInvariant `q` matches the file name only, with the same 2-character floor
 *   as the problem list search so a single keystroke cannot scan every
 *   attachment.
 */
export const CargoProblemMaterialsQuerySchema = z
  .object({
    q: z.string().trim().min(CARGO_PROBLEM_SEARCH_MIN).max(200).optional(),
    filter: CargoProblemMaterialFilterSchema.default('ALL'),
  })
  .strict();
export type CargoProblemMaterialsQuery = z.infer<typeof CargoProblemMaterialsQuerySchema>;

/** `GET` envelope for the whole material library of one problem. */
export const CargoProblemMaterialsResponseSchema = z
  .object({ items: z.array(CargoProblemMaterialSchema) })
  .strict();
export type CargoProblemMaterialsResponse = z.infer<typeof CargoProblemMaterialsResponseSchema>;

/** Envelope for a single material — the deletion path answers with the soft-deleted row. */
export const CargoProblemMaterialResponseSchema = z
  .object({ material: CargoProblemMaterialSchema })
  .strict();
export type CargoProblemMaterialResponse = z.infer<typeof CargoProblemMaterialResponseSchema>;

/**
 * @aiPurpose body for deleting a published attachment.
 * @aiSecurity the reason is shown to every participant, so it is bounded and
 *   nonblank; the global audit row stays content-free and records ids only.
 */
export const CargoProblemMaterialDeleteInputSchema = z
  .object({
    reason: z.string().trim().min(1).max(CARGO_PROBLEM_DELETE_REASON_MAX),
  })
  .strict();
export type CargoProblemMaterialDeleteInput = z.infer<typeof CargoProblemMaterialDeleteInputSchema>;
